/**
 * Pure, React-free helpers for the timed Standup view: countdown/elapsed
 * formatting, queue index math (Next / Previous / auto-advance), remaining-time
 * math, and the per-member queue projection built from the Daily Scrum roll-up.
 *
 * The ticking itself lives in the component (useEffect + setInterval); ONLY the
 * pure math + projection live here so they can be unit-tested without timers or
 * React. Do NOT put setInterval here.
 */

import type {
  ScrumEngineerGroup,
  ScrumTaskEntry,
} from '../scrum/dailyScrumModel';

/** Default per-person speaking budget, in seconds (2 minutes). */
export const DEFAULT_PER_PERSON_SECONDS = 120;

/** The minimum configurable per-person budget, in seconds. */
export const MIN_PER_PERSON_SECONDS = 15;

/** The maximum configurable per-person budget, in seconds (10 minutes). */
export const MAX_PER_PERSON_SECONDS = 600;

/** A single participant in the standup queue. */
export interface StandupParticipant {
  /** The member's alias (queue key). */
  alias: string;
  /** Display name (falls back to the alias when unknown). */
  name: string;
  /** The member's scrum lines (empty when they have no update). */
  entries: ScrumTaskEntry[];
  /** True when the member has at least one scrum entry. */
  hasUpdate: boolean;
}

/**
 * Formats a non-negative duration in seconds as `m:ss` (or `h:mm:ss` past an
 * hour). Negative inputs clamp to 0 and non-finite inputs render as `0:00`.
 */
export function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds)) return '0:00';
  const secs = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) {
    const mm = String(m).padStart(2, '0');
    return `${h}:${mm}:${ss}`;
  }
  return `${m}:${ss}`;
}

/**
 * The index of the next speaker, CLAMPED to the last position (no wrap). At the
 * end of the queue it stays put — the caller flags "time up" rather than looping
 * the standup forever. Empty/!invalid lengths return 0.
 */
export function nextIndex(current: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(current + 1, length - 1);
}

/** The index of the previous speaker, clamped to 0. */
export function previousIndex(current: number): number {
  return Math.max(0, current - 1);
}

/** True when `index` is the last position of a queue of `length`. */
export function isLastIndex(index: number, length: number): boolean {
  return length <= 0 || index >= length - 1;
}

/**
 * Remaining seconds for the current speaker: `budget - elapsed`, clamped to 0.
 * Non-finite inputs are treated as 0.
 */
export function remainingSeconds(budgetSeconds: number, elapsedSeconds: number): number {
  const budget = Number.isFinite(budgetSeconds) ? budgetSeconds : 0;
  const elapsed = Number.isFinite(elapsedSeconds) ? elapsedSeconds : 0;
  return Math.max(0, budget - elapsed);
}

/** True when the current speaker has used up (or exceeded) their budget. */
export function isTimeUp(budgetSeconds: number, elapsedSeconds: number): boolean {
  return remainingSeconds(budgetSeconds, elapsedSeconds) <= 0;
}

/**
 * The fraction (0..1) of the current speaker's budget that has ELAPSED — for a
 * progress bar. A zero/invalid budget returns 1 (fully elapsed).
 */
export function elapsedFraction(budgetSeconds: number, elapsedSeconds: number): number {
  if (!Number.isFinite(budgetSeconds) || budgetSeconds <= 0) return 1;
  const elapsed = Number.isFinite(elapsedSeconds) ? Math.max(0, elapsedSeconds) : 0;
  return Math.min(1, elapsed / budgetSeconds);
}

/** Clamps a configured per-person budget to the supported range. */
export function clampPerPersonSeconds(seconds: number): number {
  if (!Number.isFinite(seconds)) return DEFAULT_PER_PERSON_SECONDS;
  return Math.min(
    MAX_PER_PERSON_SECONDS,
    Math.max(MIN_PER_PERSON_SECONDS, Math.floor(seconds)),
  );
}

/**
 * Builds the standup queue from the Daily Scrum roll-up plus the team people
 * directory. The queue is the UNION of:
 *   • every engineer who posted a scrum update (from `groups`), and
 *   • every team member (from `people`), even with no update ("no update").
 *
 * Members with an update are ordered first; within each bucket, entries are
 * ordered alphabetically by display name (then alias) for a deterministic,
 * repeatable standup order.
 */
export function buildStandupQueue(
  groups: ScrumEngineerGroup[],
  people: { alias: string; name?: string }[],
): StandupParticipant[] {
  const nameByAlias = new Map<string, string>();
  for (const p of people ?? []) {
    if (p.alias) nameByAlias.set(p.alias, p.name?.trim() || p.alias);
  }

  const entriesByAlias = new Map<string, ScrumTaskEntry[]>();
  for (const g of groups ?? []) {
    if (g.engineer) entriesByAlias.set(g.engineer, g.entries ?? []);
  }

  const aliases = new Set<string>([
    ...entriesByAlias.keys(),
    ...nameByAlias.keys(),
  ]);

  const participants: StandupParticipant[] = Array.from(aliases).map((alias) => {
    const entries = entriesByAlias.get(alias) ?? [];
    return {
      alias,
      name: nameByAlias.get(alias) ?? alias,
      entries,
      hasUpdate: entries.length > 0,
    };
  });

  return participants.sort(
    (a, b) =>
      Number(b.hasUpdate) - Number(a.hasUpdate) ||
      a.name.localeCompare(b.name) ||
      a.alias.localeCompare(b.alias),
  );
}
